import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { calculateDistance } from '../services/api';
import { toDistanceMatrixPlace } from '../services/routeMetrics';
import {
  VEHICLES,
  DRIVER_BATA,
  getBillingDistance,
  getVehiclePrice,
  getVehicleDisplayName,
} from '../data/bookingConfig';
import { quoteOneWayPackage } from '../data/localCorridorFares';
import { coimbatoreOotyOneWayFare } from '../data/popularRoutes';
import { createSourceBookingId, createWebsiteBooking } from '../services/websiteBooking';
import { resolveTamilLocationQuery } from '../lib/locationTamil';
import { resolvePlaceFromQuery } from '../lib/resolvePlaceFromQuery';
import {
  commitRouteSummary,
  createEmptyRouteSummary,
  routeSummaryIsComplete,
} from '../lib/routeSummaryState';
import AdditionalChargesNotice from './AdditionalChargesNotice';
import '../styles/BookingForm.css';

// Get actual route distance using Google Distance Matrix API
const getRouteDistance = async (location1, location2) => {
  try {
    if (!window.google) return null;
    const service = new window.google.maps.DistanceMatrixService();
    const response = await service.getDistanceMatrix({
      origins: [toDistanceMatrixPlace(location1)],
      destinations: [toDistanceMatrixPlace(location2)],
      travelMode: window.google.maps.TravelMode.DRIVING,
      unitSystem: window.google.maps.UnitSystem.METRIC
    });
    if (response.rows?.[0]?.elements?.[0]?.status === 'OK' && response.rows[0].elements?.[0].distance) {
      return Math.round((response.rows[0].elements[0].distance.value / 1000) * 10) / 10;
    }
  } catch (error) {
    console.error('Distance Matrix error:', error);
  }
  return null;
};

const getSpeechRecognition = () => (
  typeof window === 'undefined' ? null : (window.SpeechRecognition || window.webkitSpeechRecognition || null)
);

/** Prefer Tamil recognition when the browser UI language is Tamil; otherwise Indian English. */
const getVoiceLang = () => {
  const preferred = (navigator.languages && navigator.languages.length)
    ? navigator.languages
    : [navigator.language];
  return preferred.some((lang) => /^ta\b/i.test(lang || '')) ? 'ta-IN' : 'en-IN';
};

const EMPTY_VOICE_UI = {
  open: false,
  field: null,
  status: 'idle',
  transcript: '',
  error: '',
};

// "Locality, City" from a reverse-geocode result, else its formatted address.
const describeGeocodeResult = (result) => {
  const components = result?.address_components || [];
  const pick = (...types) => components.find((c) => types.some((t) => c.types.includes(t)))?.long_name;
  const locality = pick('sublocality_level_1', 'sublocality', 'neighborhood');
  const city = pick('locality', 'administrative_area_level_2');
  const parts = [locality, city].filter((part, index, all) => part && all.indexOf(part) === index);
  return parts.length ? parts.join(', ') : result?.formatted_address || '';
};

const BookingForm = ({
  initialPickup = '',
  initialDrop = '',
  initialTripType = 'One Way',
  initialVehicle = '',
  searchButtonLabel = 'Search Cabs',
  oneWayPackage = null,
  labelWebsiteEstimate = false,
  onWebsiteEstimate = null,
}) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [tripType, setTripType] = useState(initialTripType);

  useEffect(() => {
    if (initialTripType === 'One Way' || initialTripType === 'Round Trip') {
      setTripType(initialTripType);
    }
  }, [initialTripType]);
  const [pickup, setPickup] = useState(initialPickup);
  const [dropoff, setDropoff] = useState(initialDrop);
  const [mobile, setMobile] = useState('');
  const [name, setName] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [distance, setDistance] = useState(0);
  const [routeSummary, setRouteSummary] = useState(createEmptyRouteSummary);
  const [carType, setCarType] = useState(initialVehicle);
  const [searching, setSearching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const sourceBookingIdRef = useRef(null);
  const [vehiclePrompt, setVehiclePrompt] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  const pickupInputRef = useRef(null);
  const dropoffInputRef = useRef(null);
  const pickupCoordsRef = useRef(null);
  const dropoffCoordsRef = useRef(null);
  const pickupValueRef = useRef(pickup);
  const dropoffValueRef = useRef(dropoff);
  pickupValueRef.current = pickup;
  dropoffValueRef.current = dropoff;

  // Voice input. One recognition session at a time, for one field at a time.
  const [voiceSupported] = useState(() => Boolean(getSpeechRecognition()));
  const [listeningField, setListeningField] = useState(null);
  const [voiceUi, setVoiceUi] = useState(EMPTY_VOICE_UI);
  const recognitionRef = useRef(null);
  const voiceSessionRef = useRef({
    field: null,
    baseline: '',
    cancelled: false,
    epoch: 0,
    committed: false,
    resolveStarted: false,
    lastTranscript: '',
  });
  const appliedPrefillKeyRef = useRef('');
  const autocompleteCleanupRef = useRef(null);
  const searchInFlightRef = useRef(false);

  const stopVoiceRecognition = useCallback((opts = {}) => {
    const { abort = false, cancelled = false } = opts;
    if (cancelled) voiceSessionRef.current.cancelled = true;
    const recognition = recognitionRef.current;
    if (!recognition) {
      setListeningField(null);
      return;
    }
    try {
      if (abort && typeof recognition.abort === 'function') recognition.abort();
      else recognition.stop();
    } catch (error) {
      console.error('Speech stop error:', error);
    }
  }, []);

  useEffect(() => () => {
    stopVoiceRecognition({ abort: true, cancelled: true });
    autocompleteCleanupRef.current?.();
  }, [stopVoiceRecognition]);

  const closeVoiceUi = useCallback(() => {
    setListeningField(null);
    setVoiceUi(EMPTY_VOICE_UI);
  }, []);

  /**
   * Resolve spoken text via Places, commit Pickup/Drop, then close the dialog.
   * Dialog stays open on no-match / errors so the customer can retry or Cancel.
   */
  const commitVoiceLocation = useCallback(async (field, rawText, epoch) => {
    const session = voiceSessionRef.current;
    if (session.cancelled || session.epoch !== epoch || session.committed) return;
    if (session.resolveStarted) return;
    session.resolveStarted = true;

    const englishQuery = resolveTamilLocationQuery(rawText);
    if (!englishQuery) {
      setVoiceUi({
        open: true,
        field,
        status: 'error',
        transcript: '',
        error: 'No location heard. Tap Try again, or type the location.',
      });
      session.resolveStarted = false;
      return;
    }

    const current = field === 'pickup' ? pickupValueRef.current : dropoffValueRef.current;
    if (
      current
      && current !== session.baseline
      && current !== rawText
      && current !== englishQuery
    ) {
      // User edited the field while recognition was finishing — keep their typing.
      closeVoiceUi();
      return;
    }

    setVoiceUi({
      open: true,
      field,
      status: 'processing',
      transcript: englishQuery,
      error: '',
    });

    const result = await resolvePlaceFromQuery(englishQuery);
    if (voiceSessionRef.current.cancelled || voiceSessionRef.current.epoch !== epoch) {
      return;
    }

    if (!result.ok || !result.address) {
      setVoiceUi({
        open: true,
        field,
        status: 'error',
        transcript: englishQuery,
        error: 'Could not match that location. Try again, or type the place name.',
      });
      voiceSessionRef.current.resolveStarted = false;
      return;
    }

    const address = result.address;
    if (field === 'pickup') {
      setPickup(address);
      pickupCoordsRef.current = result.coords;
      pickupValueRef.current = address;
      if (pickupInputRef.current) pickupInputRef.current.value = address;
    } else {
      setDropoff(address);
      dropoffCoordsRef.current = result.coords;
      dropoffValueRef.current = address;
      if (dropoffInputRef.current) dropoffInputRef.current.value = address;
    }
    setDistance(0);

    const other = field === 'pickup' ? dropoffValueRef.current : pickupValueRef.current;
    if (other) {
      const dist = await getRouteDistance(
        field === 'pickup' ? address : other,
        field === 'pickup' ? other : address,
      );
      if (
        !voiceSessionRef.current.cancelled
        && voiceSessionRef.current.epoch === epoch
        && dist
      ) {
        setDistance(dist);
      }
    }

    if (voiceSessionRef.current.cancelled || voiceSessionRef.current.epoch !== epoch) {
      return;
    }

    voiceSessionRef.current.committed = true;
    closeVoiceUi();
  }, [closeVoiceUi]);

  const cancelVoiceInput = () => {
    stopVoiceRecognition({ abort: true, cancelled: true });
    recognitionRef.current = null;
    voiceSessionRef.current.cancelled = true;
    closeVoiceUi();
  };

  const startVoiceInput = (field) => {
    if (recognitionRef.current) {
      // One session at a time — stop the active one instead of stacking.
      stopVoiceRecognition({ abort: true, cancelled: true });
      recognitionRef.current = null;
      closeVoiceUi();
      return;
    }

    const Recognition = getSpeechRecognition();
    if (!Recognition) {
      setVoiceUi({
        open: true,
        field,
        status: 'error',
        transcript: '',
        error: 'Voice input is not available in this browser. Please type the location.',
      });
      return;
    }

    const recognition = new Recognition();
    const voiceLang = getVoiceLang();
    recognition.lang = voiceLang;
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.maxAlternatives = 3;

    const epoch = voiceSessionRef.current.epoch + 1;
    voiceSessionRef.current = {
      field,
      baseline: field === 'pickup' ? pickupValueRef.current : dropoffValueRef.current,
      cancelled: false,
      epoch,
      committed: false,
      resolveStarted: false,
      lastTranscript: '',
    };

    const finish = () => {
      if (recognitionRef.current === recognition) recognitionRef.current = null;
      setListeningField((current) => (current === field ? null : current));
    };

    recognition.onresult = (event) => {
      const session = voiceSessionRef.current;
      if (session.cancelled || session.epoch !== epoch || session.committed) return;
      let interim = '';
      let finalText = '';
      Array.from(event.results || []).forEach((result) => {
        const chunk = result[0]?.transcript || '';
        if (result.isFinal) finalText += `${chunk} `;
        else interim += chunk;
      });
      const live = (finalText || interim).trim();
      if (live) {
        voiceSessionRef.current.lastTranscript = live;
        setVoiceUi((prev) => ({
          ...prev,
          open: true,
          field,
          status: finalText.trim() ? 'processing' : 'listening',
          transcript: live,
          error: '',
        }));
      }
      if (finalText.trim()) {
        commitVoiceLocation(field, finalText.trim(), epoch);
      }
    };

    recognition.onerror = (event) => {
      finish();
      if (voiceSessionRef.current.cancelled || voiceSessionRef.current.epoch !== epoch) {
        closeVoiceUi();
        return;
      }
      if (voiceSessionRef.current.committed || voiceSessionRef.current.resolveStarted) {
        return;
      }
      const code = event?.error;
      if (code === 'aborted') {
        closeVoiceUi();
        return;
      }
      if (code === 'no-speech') {
        setVoiceUi({
          open: true,
          field,
          status: 'error',
          transcript: '',
          error: 'No speech detected. Tap the microphone to try again, or type the location.',
        });
        return;
      }
      if (code === 'not-allowed' || code === 'service-not-allowed') {
        setVoiceUi({
          open: true,
          field,
          status: 'error',
          transcript: '',
          error: 'Microphone access is blocked. Allow the mic in your browser, or type the location.',
        });
        return;
      }
      setVoiceUi({
        open: true,
        field,
        status: 'error',
        transcript: '',
        error: 'Voice input failed. Please try again or type the location.',
      });
    };

    recognition.onend = () => {
      finish();
      const session = voiceSessionRef.current;
      if (session.cancelled || session.epoch !== epoch) {
        closeVoiceUi();
        return;
      }
      if (session.committed || session.resolveStarted) return;

      const transcript = (session.lastTranscript || '').trim();
      if (transcript) {
        // Android often ends without isFinal — resolve from the last transcript.
        setVoiceUi((prev) => ({
          ...prev,
          open: true,
          field,
          status: 'processing',
          transcript,
          error: '',
        }));
        commitVoiceLocation(field, transcript, epoch);
        return;
      }

      setVoiceUi({
        open: true,
        field,
        status: 'error',
        transcript: '',
        error: 'Listening ended. Tap the microphone to retry, or type the location.',
      });
    };

    recognitionRef.current = recognition;
    setListeningField(field);
    setVoiceUi({
      open: true,
      field,
      status: 'listening',
      transcript: '',
      error: '',
    });
    try {
      recognition.start();
    } catch (error) {
      console.error('Speech recognition error:', error);
      finish();
      setVoiceUi({
        open: true,
        field,
        status: 'error',
        transcript: '',
        error: 'Could not start voice input. Please type the location instead.',
      });
    }
  };

  // Ask for the current position once per mount. Fill pickup only while it is still empty.
  const geolocationAskedRef = useRef(false);
  useEffect(() => {
    if (geolocationAskedRef.current || initialPickup || !navigator.geolocation) return;
    geolocationAskedRef.current = true;

    const fillPickup = (coords) => {
      if (!window.google || pickupValueRef.current) return;
      new window.google.maps.Geocoder().geocode({ location: coords }, async (results, status) => {
        if (status !== 'OK' || !results?.length || pickupValueRef.current) return;
        const address = describeGeocodeResult(results[0]);
        if (!address) return;
        pickupCoordsRef.current = coords;
        setPickup(address);
        if (dropoffValueRef.current) {
          const dist = await getRouteDistance(address, dropoffValueRef.current);
          if (dist) setDistance(dist);
        }
      });
    };

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const coords = { lat: position.coords.latitude, lng: position.coords.longitude };
        if (window.google) {
          fillPickup(coords);
        } else {
          window.addEventListener('google-maps-ready', () => fillPickup(coords), { once: true });
        }
      },
      () => {
        // Denied or unavailable: the pickup field stays manual.
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 },
    );
  }, [initialPickup]);

  // Google Places Autocomplete — re-bind when step 1 mounts (including after "Change Route").
  // Listeners use value refs so fare search cannot lose the paired location to a stale closure.
  useEffect(() => {
    if (step !== 1) return undefined;

    let cancelled = false;
    const listeners = [];

    const initAutocomplete = () => {
      if (cancelled || !window.google || !pickupInputRef.current || !dropoffInputRef.current) return;

      autocompleteCleanupRef.current?.();

      const options = {
        componentRestrictions: { country: 'in' },
        fields: ['formatted_address', 'geometry', 'place_id', 'name'],
      };
      const pickupAC = new window.google.maps.places.Autocomplete(pickupInputRef.current, options);
      const dropoffAC = new window.google.maps.places.Autocomplete(dropoffInputRef.current, options);

      listeners.push(
        pickupAC.addListener('place_changed', async () => {
          const place = pickupAC.getPlace();
          const typed = (pickupInputRef.current?.value || '').trim();
          if (!place?.geometry) {
            if (typed) {
              setPickup(typed);
              pickupCoordsRef.current = null;
            }
            return;
          }
          const coords = { lat: place.geometry.location.lat(), lng: place.geometry.location.lng() };
          pickupCoordsRef.current = coords;
          const address = place.formatted_address || place.name || typed;
          setPickup(address);
          const other = dropoffValueRef.current;
          if (other) {
            const dist = await getRouteDistance(address, other);
            if (dist) setDistance(dist);
          }
        }),
        dropoffAC.addListener('place_changed', async () => {
          const place = dropoffAC.getPlace();
          const typed = (dropoffInputRef.current?.value || '').trim();
          if (!place?.geometry) {
            if (typed) {
              setDropoff(typed);
              dropoffCoordsRef.current = null;
            }
            return;
          }
          const coords = { lat: place.geometry.location.lat(), lng: place.geometry.location.lng() };
          dropoffCoordsRef.current = coords;
          const address = place.formatted_address || place.name || typed;
          setDropoff(address);
          const other = pickupValueRef.current;
          if (other) {
            const dist = await getRouteDistance(other, address);
            if (dist) setDistance(dist);
          }
        }),
      );

      autocompleteCleanupRef.current = () => {
        listeners.forEach((listener) => {
          window.google?.maps?.event?.removeListener?.(listener);
        });
        listeners.length = 0;
      };
    };

    if (window.google) {
      initAutocomplete();
    } else {
      window.addEventListener('google-maps-ready', initAutocomplete, { once: true });
    }

    return () => {
      cancelled = true;
      window.removeEventListener('google-maps-ready', initAutocomplete);
      autocompleteCleanupRef.current?.();
      autocompleteCleanupRef.current = null;
    };
  }, [step]);

  useEffect(() => {
    if (initialVehicle) setCarType(initialVehicle);
  }, [initialVehicle]);

  const skipStepScroll = useRef(true);

  useEffect(() => {
    document.body.classList.toggle('booking-flow-active', step > 1);
    if (skipStepScroll.current) {
      skipStepScroll.current = false;
      return;
    }

    // After Get Fare Estimate, bring the full route summary into view below the
    // sticky header + trip tabs (Android often leaves Pickup clipped otherwise).
    const revealResultsTop = () => {
      if (step === 2) {
        const summary = document.querySelector('.bf-route-summary');
        if (summary) {
          summary.scrollIntoView({ block: 'start', behavior: 'auto' });
          return;
        }
      }
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(revealResultsTop);
    });
  }, [step]);

  useEffect(() => () => {
    document.body.classList.remove('booking-flow-active');
  }, []);

  useEffect(() => {
    if (!vehiclePrompt) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') setVehiclePrompt(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [vehiclePrompt]);

  // Apply parent prefills only when the pair actually changes. Do not reset
  // user-typed values on unrelated re-renders or after Get Fare Estimate.
  useEffect(() => {
    if (!initialPickup || !initialDrop) return undefined;

    const key = `${initialPickup}||${initialDrop}`;
    if (appliedPrefillKeyRef.current === key) return undefined;
    appliedPrefillKeyRef.current = key;

    setPickup(initialPickup);
    setDropoff(initialDrop);
    pickupCoordsRef.current = null;
    dropoffCoordsRef.current = null;
    pickupValueRef.current = initialPickup;
    dropoffValueRef.current = initialDrop;
    setStep(1);
    setDistance(0);

    let cancelled = false;

    const prefetchDistance = async () => {
      const stillMatchesDefaults = () =>
        pickupValueRef.current === initialPickup &&
        dropoffValueRef.current === initialDrop;

      const dist = await getRouteDistance(initialPickup, initialDrop);
      if (cancelled || !stillMatchesDefaults()) return;
      if (dist) {
        setDistance(dist);
        return;
      }
      const calcDist = await calculateDistance(initialPickup, initialDrop);
      if (!cancelled && stillMatchesDefaults() && calcDist) setDistance(calcDist);
    };

    if (window.google) {
      prefetchDistance();
      return () => { cancelled = true; };
    }

    window.addEventListener('google-maps-ready', prefetchDistance, { once: true });
    return () => {
      cancelled = true;
      window.removeEventListener('google-maps-ready', prefetchDistance);
    };
  }, [initialPickup, initialDrop]);

  // Billing distance:
  //   One Way  → max(actual, 130)  — minimum 130 km
  //   Round Trip → max(actual, 130) × 2  — apply minimum, then double
  const getBaseDistance = () => getBillingDistance(distance, tripType);

  const getPrice = (rate) => getVehiclePrice(distance, rate, tripType);

  const oneWayPackageActive = tripType === 'One Way' && Boolean(oneWayPackage?.oneWayPackage);
  const showWebsiteEstimate = labelWebsiteEstimate && !oneWayPackageActive;

  const reportWebsiteEstimate = useCallback((actualDistance, nextTripType) => {
    if (!onWebsiteEstimate) return;
    if (nextTripType === 'One Way' && oneWayPackage?.oneWayPackage) {
      onWebsiteEstimate(null);
      return;
    }
    const actual = Number(actualDistance);
    if (!Number.isFinite(actual) || actual <= 0) {
      onWebsiteEstimate(null);
      return;
    }
    const billedKm = getBillingDistance(actual, nextTripType);
    onWebsiteEstimate({
      actualKm: actual,
      billedKm,
      tripType: nextTripType,
      vehicles: VEHICLES.map((vehicle) => ({
        type: vehicle.type,
        rate: vehicle.rate,
        total: getVehiclePrice(actual, vehicle.rate, nextTripType),
      })),
    });
  }, [onWebsiteEstimate, oneWayPackage]);

  useEffect(() => {
    reportWebsiteEstimate(distance, tripType);
  }, [distance, tripType, reportWebsiteEstimate]);

  const visibleVehicles = oneWayPackageActive
    ? VEHICLES.filter((vehicle) => oneWayPackage.vehicles[vehicle.type] != null)
    : VEHICLES;

  const buildFare = (vehicleType) => {
    if (!oneWayPackageActive && tripType === 'One Way') {
      const fixed = coimbatoreOotyOneWayFare(pickup, dropoff, vehicleType);
      if (fixed != null) {
        return {
          packageQuote: {
            base: fixed,
            extraKm: 0,
            extraFare: 0,
            total: fixed,
            includedDistanceKm: Math.max(1, Math.round(Number(distance) || 0)),
            extraRate: 0,
            chargeSummaryLabel: 'Tolls + Parking',
            chargeSummaryValue: 'Included',
            chargeNote: 'Tolls + Parking included.',
            detailsCharge: 'Tolls + Parking included',
          },
          baseDistance: getBaseDistance(),
          ratePerKm: 0,
          tripFare: fixed,
          driverBata: 0,
          totalCost: fixed,
          notes: 'Tolls + Parking included',
        };
      }
    }

    const packageQuote = oneWayPackageActive
      ? quoteOneWayPackage(oneWayPackage, vehicleType, distance)
      : null;

    if (packageQuote) {
      return {
        packageQuote,
        baseDistance: packageQuote.distanceKnown ? distance : oneWayPackage.includedDistanceKm,
        ratePerKm: packageQuote.extraRate,
        tripFare: packageQuote.base,
        driverBata: 0,
        totalCost: packageQuote.total,
        notes: packageQuote.bookingNotes,
      };
    }

    const billed = getBaseDistance();
    const ratePerKm = VEHICLES.find((vehicle) => vehicle.type === vehicleType)?.rate || 14;
    const tripFare = Math.round(billed * ratePerKm);
    return {
      packageQuote: null,
      baseDistance: billed,
      ratePerKm,
      tripFare,
      driverBata: DRIVER_BATA,
      totalCost: tripFare + DRIVER_BATA,
      notes: 'Toll / Parking / Waiting Time extra',
    };
  };

  const selectTripType = (nextType) => {
    setTripType(nextType);
    if (
      nextType === 'One Way'
      && oneWayPackage?.oneWayPackage
      && carType
      && oneWayPackage.vehicles[carType] == null
    ) {
      setCarType('');
    }
  };

  // Step 1: search cabs
  const handleSearch = async (e) => {
    e.preventDefault();
    if (searchInFlightRef.current || searching) return;

    // Places Autocomplete can update the DOM without always syncing React state.
    // Read the live input values so Get Fare Estimate never drops a typed location.
    const nextPickup = resolveTamilLocationQuery(
      (pickupInputRef.current?.value || pickup || '').trim(),
    );
    const nextDropoff = resolveTamilLocationQuery(
      (dropoffInputRef.current?.value || dropoff || '').trim(),
    );
    const nextMobile = (mobile || '').trim();

    if (nextPickup !== pickup) setPickup(nextPickup);
    if (nextDropoff !== dropoff) setDropoff(nextDropoff);
    pickupValueRef.current = nextPickup;
    dropoffValueRef.current = nextDropoff;

    if (!nextPickup || !nextDropoff || !nextMobile) {
      Swal.fire({ title: 'Error', text: 'Please fill all fields!', icon: 'error' });
      return;
    }
    if (!/^\d{10}$/.test(nextMobile)) {
      Swal.fire({ title: 'Error', text: 'Please enter a valid 10-digit mobile number!', icon: 'error' });
      return;
    }

    searchInFlightRef.current = true;
    let nextDistance = distance;

    // If coords already available from autocomplete, distance is already set
    if (!nextDistance) {
      setSearching(true);
      try {
        const dist = await getRouteDistance(nextPickup, nextDropoff);
        if (dist && dist > 0) {
          nextDistance = dist;
          setDistance(dist);
        } else {
          const calcDist = await calculateDistance(nextPickup, nextDropoff);
          if (calcDist && calcDist > 0) {
            nextDistance = calcDist;
            setDistance(calcDist);
          }
        }
      } catch (err) {
        console.error('Distance calculation error:', err);
        Swal.fire({
          title: 'Could not estimate distance',
          text: 'Your pickup, drop and mobile number were kept. Check the locations and try again.',
          icon: 'warning',
        });
        setSearching(false);
        searchInFlightRef.current = false;
        return;
      }
      setSearching(false);
    }

    // Re-assert values after async work so a late Places/geocode callback cannot blank them.
    setPickup(nextPickup);
    setDropoff(nextDropoff);
    pickupValueRef.current = nextPickup;
    dropoffValueRef.current = nextDropoff;

    if (!nextDistance || nextDistance <= 0) {
      Swal.fire({
        title: 'Could not estimate distance',
        text: 'Your pickup, drop and mobile number were kept. Check the locations and try again.',
        icon: 'warning',
      });
      searchInFlightRef.current = false;
      return;
    }

    const summary = commitRouteSummary(createEmptyRouteSummary(), {
      pickup: nextPickup,
      dropoff: nextDropoff,
      distance: nextDistance,
    });
    if (!routeSummaryIsComplete(summary)) {
      Swal.fire({
        title: 'Route incomplete',
        text: 'Pickup and drop locations are required before showing fares. Please check both fields.',
        icon: 'warning',
      });
      searchInFlightRef.current = false;
      return;
    }

    setRouteSummary(summary);
    setDistance(summary.distance);
    searchInFlightRef.current = false;
    setStep(2);
  };

  const clearFieldError = (field) => {
    setFieldErrors((prev) => (prev[field] ? { ...prev, [field]: '' } : prev));
  };

  // Step 2: booking details. Fare math is unchanged; this only gates the review step.
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!carType) {
      setVehiclePrompt(true);
      return;
    }

    const errors = {};
    if (!date) errors.date = 'Please select a pickup date.';
    if (!time) errors.time = 'Please select a pickup time.';
    if (!name.trim()) errors.name = 'Please enter your name.';
    if (!/^\d{10}$/.test(mobile)) errors.mobile = 'Please enter a valid 10-digit mobile number.';
    setFieldErrors(errors);

    const firstInvalid = ['date', 'time', 'name', 'mobile'].find((field) => errors[field]);
    if (firstInvalid) {
      const fieldId = firstInvalid === 'mobile' ? 'mobile-booking' : firstInvalid;
      const field = document.getElementById(fieldId);
      if (field) field.focus();
      return;
    }

    setStep(3);
  };

  const placeLabel = (value) => {
    if (!value) return '';
    const first = value.split(',')[0].trim();
    return first || value;
  };

  const formatReviewDate = (value) => {
    if (!value) return '';
    const [year, month, day] = value.split('-');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthIndex = Number(month) - 1;
    if (!year || !day || monthIndex < 0 || monthIndex > 11) return value;
    return `${Number(day)} ${months[monthIndex]} ${year}`;
  };

  // Convert 24-hour time format to 12-hour format with AM/PM
  const convertTo12HourFormat = (timeStr) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  // Step 3: confirm booking from summary
  const handleConfirmBooking = async (e) => {
    e.preventDefault();
    if (submitting) return;

    const fare = buildFare(carType);
    const {
      baseDistance,
      ratePerKm,
      tripFare,
      driverBata,
      totalCost,
      notes,
    } = fare;
    if (!sourceBookingIdRef.current) {
      sourceBookingIdRef.current = createSourceBookingId();
    }

    setSubmitting(true);
    Swal.fire({
      title: 'Processing Booking...',
      html: 'Saving your booking details...',
      icon: 'info',
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => { Swal.showLoading(); }
    });

    try {
      const result = await createWebsiteBooking({
        sourceBookingId: sourceBookingIdRef.current,
        customerName: name.trim(),
        customerMobile: mobile,
        tripType,
        pickup,
        drop: dropoff,
        tripDate: date,
        pickupTime: convertTo12HourFormat(time),
        vehicleType: carType,
        distanceKm: Number(baseDistance.toFixed(2)),
        ratePerKm,
        tripFare,
        driverBata,
        totalFare: totalCost,
        notes,
      });

      Swal.close();
      navigate('/confirm-success', {
        state: {
          bookingData: {
            referenceId: result.referenceId,
            referenceNumber: result.referenceId,
            sourceBookingId: result.sourceBookingId,
            name,
            mobile,
            pickup,
            dropoff,
            dateTime: `${new Date(date).toLocaleDateString('en-GB')} at ${convertTo12HourFormat(time)}`,
            carType,
            distance: baseDistance.toFixed(2),
            totalCost,
            tripType,
          },
        },
      });
    } catch (error) {
      setSubmitting(false);
      Swal.fire({
        title: 'Booking not confirmed',
        text: error.message || 'Booking could not be saved. Please try again.',
        icon: 'error',
      });
    }
  };

  const today = new Date().toISOString().split('T')[0];

  return (
    <nav>
      {/* Trip Type Tabs — hidden on the short review so that screen stays compact */}
      {step < 3 && (
      <div className={`nav nav-tabs bf-trip-tabs${step === 2 ? ' bf-trip-tabs--sticky' : ''}`} id="nav-tab">
        <div
          className={`nav-link ${tripType === 'One Way' ? 'active' : ''}`}
          onClick={() => selectTripType('One Way')}
          style={{ cursor: 'pointer' }}
        >
          <div className="twm-tabs-bx">
            <div className="tabs-title">
              One Way Drop
            </div>
          </div>
        </div>
        <div
          className={`nav-link ${tripType === 'Round Trip' ? 'active' : ''}`}
          onClick={() => selectTripType('Round Trip')}
          style={{ cursor: 'pointer' }}
        >
          <div className="twm-tabs-bx">
            <div className="tabs-title">
              Round Trip
            </div>
          </div>
        </div>
      </div>
      )}

      <div className="tab-content">
        <div className="tab-pane fade show active">
          <div className={`twm-tabs-search-section${step > 1 ? ' bf-flow' : ''}`}>

            {/* ── STEP 1: Route & Mobile ── */}
            {step === 1 && (
              <form onSubmit={handleSearch}>
                {/* Pick up & Drop off */}
                <div className="row m-b20">
                  <div className="col-xxl-6 col-xl-6 col-lg-6 col-sm-6">
                    <div className="form-group location-input">
                      <label htmlFor="pickup">
                        <i className="fa fa-map-marker"></i> Pick up Location
                      </label>
                      <div className="bf-voice-field">
                        <input
                          ref={pickupInputRef}
                          id="pickup"
                          className="form-control"
                          placeholder="Enter pickup location"
                          value={pickup}
                          onChange={(e) => { setPickup(e.target.value); pickupCoordsRef.current = null; setDistance(0); }}
                          autoComplete="off"
                          required
                        />
                        {voiceSupported && (
                          <button
                            type="button"
                            className={`bf-mic-btn${listeningField === 'pickup' ? ' bf-mic-btn--listening' : ''}`}
                            onClick={() => startVoiceInput('pickup')}
                            aria-pressed={listeningField === 'pickup'}
                            aria-label={listeningField === 'pickup' ? 'Stop listening for pickup' : 'Speak pickup location'}
                            title="Speak pickup location"
                          >
                            <i className="fa fa-microphone" aria-hidden="true"></i>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="col-xxl-6 col-xl-6 col-lg-6 col-sm-6">
                    <div className="form-group location-input">
                      <label htmlFor="dropoff">
                        <i className="fa fa-map-marker"></i> Drop off Location
                      </label>
                      <div className="bf-voice-field">
                        <input
                          ref={dropoffInputRef}
                          id="dropoff"
                          className="form-control"
                          placeholder="Enter drop location"
                          value={dropoff}
                          onChange={(e) => { setDropoff(e.target.value); dropoffCoordsRef.current = null; setDistance(0); }}
                          autoComplete="off"
                          required
                        />
                        {voiceSupported && (
                          <button
                            type="button"
                            className={`bf-mic-btn${listeningField === 'dropoff' ? ' bf-mic-btn--listening' : ''}`}
                            onClick={() => startVoiceInput('dropoff')}
                            aria-pressed={listeningField === 'dropoff'}
                            aria-label={listeningField === 'dropoff' ? 'Stop listening for drop-off' : 'Speak drop location'}
                            title="Speak drop location"
                          >
                            <i className="fa fa-microphone" aria-hidden="true"></i>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Mobile */}
                <div className="row m-b20">
                  <div className="col-xxl-12 col-xl-12 col-lg-12">
                    <div className="form-group">
                      <label htmlFor="mobile">
                        <i className="fa fa-phone"></i> Mobile Number
                      </label>
                      <input
                        id="mobile"
                        className="form-control"
                        type="tel"
                        maxLength="10"
                        placeholder="Enter your 10-digit mobile number"
                        value={mobile}
                        onChange={(e) => setMobile(e.target.value.replace(/\D/g, ''))}
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className="twm-tabs-search-btn form-btn">
                  <button type="submit" className="site-button1" disabled={searching}>
                    {searching ? 'Searching...' : searchButtonLabel}
                  </button>
                </div>
              </form>
            )}

            {/* ── STEP 2: Vehicle + Details ── */}
            {step === 2 && (
              <form onSubmit={handleSubmit}>
                <div className="bf-route-summary" aria-label="Selected route">
                  <div className="bf-route-line">
                    <span className="bf-route-k">Pickup:</span>
                    <span className="bf-route-v">
                      {routeSummary.pickup || pickup || 'Not set — change route to enter pickup'}
                    </span>
                  </div>
                  <div className="bf-route-arrow" aria-hidden="true">↓</div>
                  <div className="bf-route-line">
                    <span className="bf-route-k">Drop:</span>
                    <span className="bf-route-v">
                      {routeSummary.dropoff || dropoff || 'Not set — change route to enter drop'}
                    </span>
                  </div>
                  <div className="bf-route-line">
                    <span className="bf-route-k">Distance:</span>
                    <span className="bf-route-v">
                      {(routeSummary.distance || distance) > 0
                        ? `${oneWayPackageActive
                          ? Math.round(routeSummary.distance || distance)
                          : Math.round(getBillingDistance(routeSummary.distance || distance, tripType))} KM`
                        : 'Unavailable — change route and try again'}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="bf-back-link"
                    onClick={() => {
                      setRouteSummary(createEmptyRouteSummary());
                      setStep(1);
                    }}
                  >
                    Change route
                  </button>
                </div>

                <p className="bf-select-label">Select Vehicle</p>
                <div className="bf-vehicle-cards">
                  {visibleVehicles.map((v) => {
                    const fare = buildFare(v.type);
                    const detailsHtml = fare.packageQuote
                      ? `<p>One-way package: ₹${fare.packageQuote.base.toLocaleString('en-IN')}</p><p>Included distance: ${fare.packageQuote.includedDistanceKm} km</p><p>Extra distance: ${fare.packageQuote.extraKm.toFixed(1)} km at ₹${fare.packageQuote.extraRate}/km</p><p>${fare.packageQuote.detailsCharge}</p><p><strong>Total: ₹${fare.totalCost.toLocaleString('en-IN')}</strong></p>`
                      : `<p>Rate: ₹${v.rate}/km</p><p>Driver Bata: ₹${DRIVER_BATA}</p><p>Distance: ${getBaseDistance().toFixed(0)} km</p><p><strong>Total: ₹${getPrice(v.rate).toLocaleString('en-IN')}</strong></p>`;
                    return (
                    <div
                      key={v.type}
                      className={`bf-vehicle-card ${carType === v.type ? 'bf-vehicle-card--selected' : ''}`}
                      onClick={() => {
                        setCarType(v.type);
                        setVehiclePrompt(false);
                      }}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setCarType(v.type);
                          setVehiclePrompt(false);
                        }
                      }}
                    >
                      <img src={v.image} alt="" className="bf-vehicle-img" />
                      <div className="bf-vehicle-name">{getVehicleDisplayName(v)}</div>
                      <div className="bf-vehicle-price">
                        <span>
                          {showWebsiteEstimate ? 'Estimate ' : ''}
                          ₹{fare.totalCost.toLocaleString('en-IN')}
                        </span>
                        <button
                          type="button"
                          className="bf-more-link"
                          onClick={(e) => {
                            e.stopPropagation();
                            Swal.fire({
                              title: `${getVehicleDisplayName(v)} Details`,
                              html: detailsHtml,
                              icon: 'info'
                            });
                          }}
                        >
                          More
                        </button>
                      </div>
                    </div>
                    );
                  })}
                </div>

                {carType && (
                  <div className="bf-details">
                    <div className="bf-details-grid">
                      <div className="form-group">
                        <label htmlFor="date">Pickup Date</label>
                        <input
                          id="date"
                          className={`form-control${fieldErrors.date ? ' is-invalid' : ''}`}
                          type="date"
                          value={date}
                          onChange={(e) => { setDate(e.target.value); clearFieldError('date'); }}
                          min={today}
                          aria-invalid={fieldErrors.date ? 'true' : 'false'}
                        />
                        {fieldErrors.date && <p className="bf-field-error">{fieldErrors.date}</p>}
                      </div>
                      <div className="form-group">
                        <label htmlFor="time">Pickup Time</label>
                        <input
                          id="time"
                          className={`form-control${fieldErrors.time ? ' is-invalid' : ''}`}
                          type="time"
                          value={time}
                          onChange={(e) => { setTime(e.target.value); clearFieldError('time'); }}
                          aria-invalid={fieldErrors.time ? 'true' : 'false'}
                        />
                        {fieldErrors.time && <p className="bf-field-error">{fieldErrors.time}</p>}
                      </div>
                      <div className="form-group bf-span-2">
                        <label htmlFor="name">Name</label>
                        <input
                          id="name"
                          className={`form-control${fieldErrors.name ? ' is-invalid' : ''}`}
                          placeholder="Your name"
                          value={name}
                          onChange={(e) => { setName(e.target.value); clearFieldError('name'); }}
                          aria-invalid={fieldErrors.name ? 'true' : 'false'}
                        />
                        {fieldErrors.name && <p className="bf-field-error">{fieldErrors.name}</p>}
                      </div>
                      <div className="form-group bf-span-2">
                        <label htmlFor="mobile-booking">Mobile Number</label>
                        <input
                          id="mobile-booking"
                          className={`form-control${fieldErrors.mobile ? ' is-invalid' : ''}`}
                          type="tel"
                          inputMode="numeric"
                          maxLength="10"
                          placeholder="10-digit mobile number"
                          value={mobile}
                          onChange={(e) => { setMobile(e.target.value.replace(/\D/g, '')); clearFieldError('mobile'); }}
                          aria-invalid={fieldErrors.mobile ? 'true' : 'false'}
                        />
                        {fieldErrors.mobile && <p className="bf-field-error">{fieldErrors.mobile}</p>}
                      </div>
                    </div>
                  </div>
                )}

                <div className="twm-tabs-search-btn form-btn">
                  <button type="submit" className="site-button1">Book Now</button>
                </div>
                <AdditionalChargesNotice tripType={tripType} />
              </form>
            )}

            {/* ── STEP 3: Review Your Booking ── */}
            {step === 3 && (() => {
              const fare = buildFare(carType);
              const totalLabel = fare.totalCost.toLocaleString('en-IN');
              const tripFareLabel = fare.tripFare.toLocaleString('en-IN', { maximumFractionDigits: 0 });

              return (
                <div className="bf-review">
                  <h2>Booking Summary</h2>
                  <dl className="bf-summary-list">
                    <div className="bf-summary-row">
                      <dt>Pickup</dt>
                      <dd>{placeLabel(pickup)}</dd>
                    </div>
                    <div className="bf-summary-row">
                      <dt>Drop</dt>
                      <dd>{placeLabel(dropoff)}</dd>
                    </div>
                    <div className="bf-summary-row">
                      <dt>Date</dt>
                      <dd>{formatReviewDate(date)}</dd>
                    </div>
                    <div className="bf-summary-row">
                      <dt>Time</dt>
                      <dd>{convertTo12HourFormat(time)}</dd>
                    </div>
                    {oneWayPackage && (
                      <div className="bf-summary-row">
                        <dt>Trip</dt>
                        <dd>{tripType === 'One Way' ? 'One Way Drop' : 'Round Trip'}</dd>
                      </div>
                    )}
                    <div className="bf-summary-row">
                      <dt>Vehicle</dt>
                      <dd>{getVehicleDisplayName(carType)}</dd>
                    </div>
                    <div className="bf-summary-row">
                      <dt>Passenger</dt>
                      <dd>{name}</dd>
                    </div>
                    <div className="bf-summary-row">
                      <dt>Fare</dt>
                      <dd>₹{totalLabel}</dd>
                    </div>
                  </dl>

                  <div className="bf-fare-box">
                    <p className="bf-fare-title">{showWebsiteEstimate ? 'Fare estimate' : 'Fare Summary'}</p>
                    {fare.packageQuote ? (
                      <>
                        <div className="bf-fare-row">
                          <span>One-way package</span>
                          <span>₹{tripFareLabel}</span>
                        </div>
                        <div className="bf-fare-row">
                          <span>
                            Extra distance
                            {fare.packageQuote.extraKm > 0
                              ? ` (${fare.packageQuote.extraKm.toFixed(1)} km)`
                              : ` (within ${fare.packageQuote.includedDistanceKm} km)`}
                          </span>
                          <span>₹{fare.packageQuote.extraFare.toLocaleString('en-IN')}</span>
                        </div>
                        <div className="bf-fare-row">
                          <span>{fare.packageQuote.chargeSummaryLabel}</span>
                          <span>{fare.packageQuote.chargeSummaryValue}</span>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="bf-fare-row">
                          <span>Trip Fare</span>
                          <span>₹{tripFareLabel}</span>
                        </div>
                        <div className="bf-fare-row">
                          <span>Driver Bata</span>
                          <span>₹{DRIVER_BATA}</span>
                        </div>
                      </>
                    )}
                    <div className="bf-fare-row bf-fare-row--total">
                      <span>{showWebsiteEstimate ? 'Estimated total' : 'Total Amount'}</span>
                      <span>₹{totalLabel}</span>
                    </div>
                  </div>
                  <p className="bf-fare-note">
                    {fare.packageQuote
                      ? fare.packageQuote.chargeNote
                      : showWebsiteEstimate
                        ? 'This is a website estimate, not a fixed package fare. Toll, parking, permit, hill station and waiting time are extra. Minimum billing distance is 130 km.'
                        : 'Toll, parking, permit, hill station and waiting time are extra. Minimum billing distance is 130 km.'}
                  </p>

                  <div className="bf-review-actions">
                    <button
                      type="button"
                      className="review-btn review-btn-primary"
                      onClick={handleConfirmBooking}
                      disabled={submitting}
                    >
                      <i className="fa fa-check"></i> Confirm Booking
                    </button>
                    <button
                      type="button"
                      className="review-btn review-btn-secondary"
                      onClick={() => setStep(2)}
                    >
                      <i className="fa fa-arrow-left"></i> Go Back & Edit
                    </button>
                  </div>
                </div>
              );
            })()}

          </div>
        </div>
      </div>

      {voiceUi.open && (
        <div
          className="bf-voice-backdrop"
          role="presentation"
          onClick={cancelVoiceInput}
        >
          <div
            className="bf-voice-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="bf-voice-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className={`bf-voice-orb${voiceUi.status === 'listening' ? ' bf-voice-orb--live' : ''}`}
              aria-hidden="true"
            >
              <i className="fa fa-microphone" />
            </div>
            <h3 id="bf-voice-title">
              {voiceUi.status === 'error'
                ? 'Voice input'
                : voiceUi.status === 'processing'
                  ? 'Matching location…'
                  : 'Listening…'}
            </h3>
            <p className="bf-voice-field-hint">
              {voiceUi.field === 'pickup' ? 'Pickup location' : 'Drop-off location'}
              {getVoiceLang() === 'ta-IN'
                ? ' · Speak in Tamil or English'
                : ' · Speak in English (Tamil works when the browser supports it)'}
            </p>
            {voiceUi.transcript ? (
              <p className="bf-voice-transcript" aria-live="polite">{voiceUi.transcript}</p>
            ) : (
              <p className="bf-voice-transcript bf-voice-transcript--muted" aria-live="polite">
                {voiceUi.status === 'listening' ? 'Say a place name…' : ''}
              </p>
            )}
            {voiceUi.error ? (
              <p className="bf-voice-error" role="alert">{voiceUi.error}</p>
            ) : null}
            <div className="bf-voice-actions">
              {voiceUi.status === 'error' ? (
                <button
                  type="button"
                  className="bf-voice-retry"
                  onClick={() => startVoiceInput(voiceUi.field || 'pickup')}
                >
                  Try again
                </button>
              ) : null}
              <button type="button" className="bf-voice-cancel" onClick={cancelVoiceInput}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {vehiclePrompt && (
        <div
          className="bf-modal-backdrop"
          role="presentation"
          onClick={() => setVehiclePrompt(false)}
        >
          <div
            className="bf-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="bf-vehicle-prompt-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="bf-vehicle-prompt-title">Please Select a Vehicle</h3>
            <p>Please select a vehicle type to continue with your booking.</p>
            <button type="button" className="bf-modal-btn" onClick={() => setVehiclePrompt(false)}>
              Select Vehicle
            </button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default BookingForm;

